import React from 'react';
import { UseFormReturn, FieldValues, Path } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '@radix-ui/react-icons';

import { Box, Button, Stack, Heading, Text, Progress } from '../../index';

interface WizardStep<T extends FieldValues> {
  id: string;
  title: string;
  description?: string;
  fields?: Path<T>[];
  validate?: (data: T) => boolean | Promise<boolean>;
  canSkip?: boolean;
  component: React.ComponentType<{
    form: UseFormReturn<T>;
    goNext: () => void;
    goPrevious: () => void;
  }>;
}

interface FormWizardProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  steps: WizardStep<T>[];
  onComplete: (data: T) => Promise<void>;
  onStepChange?: (step: number, stepId: string) => void;
  showProgress?: boolean;
  showStepIndicator?: boolean;
  allowNavigation?: boolean;
  persistStep?: boolean;
  storageKey?: string;
}

export function FormWizard<T extends FieldValues>({
  form,
  steps,
  onComplete,
  onStepChange,
  showProgress = true,
  showStepIndicator = true,
  allowNavigation = false,
  persistStep = false,
  storageKey = 'form-wizard-step',
}: FormWizardProps<T>) {
  const [currentStep, setCurrentStep] = React.useState(() => {
    if (persistStep && typeof window !== 'undefined') {
      const savedStep = localStorage.getItem(storageKey);
      return savedStep ? parseInt(savedStep, 10) : 0;
    }
    return 0;
  });

  const [completedSteps, setCompletedSteps] = React.useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Persist current step
  React.useEffect(() => {
    if (persistStep && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, currentStep.toString());
    }
    onStepChange?.(currentStep, currentStepData.id);
  }, [currentStep, currentStepData.id, persistStep, storageKey, onStepChange]);

  // Validate current step
  const validateCurrentStep = async () => {
    const { trigger, getValues } = form;

    // Field-level validation
    if (currentStepData.fields) {
      const isValid = await trigger(currentStepData.fields);
      if (!isValid) return false;
    }

    // Custom validation
    if (currentStepData.validate) {
      const data = getValues();
      const isValid = await currentStepData.validate(data);
      if (!isValid) return false;
    }

    return true;
  };

  // Navigation handlers
  const goNext = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid && !currentStepData.canSkip) return;

    setCompletedSteps((prev) => new Set(prev).add(currentStep));

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const goPrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = async (stepIndex: number) => {
    if (!allowNavigation) return;

    // Can only go to completed steps or the next step
    if (stepIndex <= currentStep || completedSteps.has(stepIndex - 1)) {
      setCurrentStep(stepIndex);
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;

    setIsSubmitting(true);

    try {
      const data = form.getValues();
      await onComplete(data);

      // Clear persisted step
      if (persistStep && typeof window !== 'undefined') {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const CurrentStepComponent = currentStepData.component;

  return (
    <Box>
      {showStepIndicator && (
        <StepIndicator
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={allowNavigation ? goToStep : undefined}
        />
      )}

      {showProgress && (
        <Box marginBottom="24px">
          <Box height="4px" borderRadius="2px" overflow="hidden" backgroundColor="#E5E7EB">
            <Progress value={((currentStep + 1) / steps.length) * 100} />
          </Box>
        </Box>
      )}

      <Box marginBottom="24px">
        <Heading size="lg" marginBottom="8px">
          {currentStepData.title}
        </Heading>
        {currentStepData.description && <Text color="#6B7280">{currentStepData.description}</Text>}
      </Box>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          <CurrentStepComponent form={form} goNext={goNext} goPrevious={goPrevious} />
        </motion.div>
      </AnimatePresence>

      <Stack direction="row" marginTop="32px" justifyContent="space-between">
        <Button
          variant="outline"
          onClick={goPrevious}
          disabled={isFirstStep}
          leftIcon={<ChevronLeftIcon />}
        >
          Geri
        </Button>

        <Stack direction="row" gap="12px">
          {currentStepData.canSkip && !isLastStep && (
            <Button variant="ghost" onClick={() => setCurrentStep((prev) => prev + 1)}>
              Atla
            </Button>
          )}

          <Button
            colorPalette="blue"
            onClick={goNext}
            isLoading={isSubmitting}
            rightIcon={isLastStep ? <CheckIcon /> : <ChevronRightIcon />}
          >
            {isLastStep ? 'Tamamla' : 'İleri'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// Step indicator component
interface StepIndicatorProps<T extends FieldValues = FieldValues> {
  steps: WizardStep<T>[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick?: (index: number) => void;
}

function StepIndicator<T extends FieldValues = FieldValues>({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
}: StepIndicatorProps<T>) {
  return (
    <Box marginBottom="32px">
      <Stack direction="row" gap="0" position="relative">
        {/* Progress line */}
        <Box
          position="absolute"
          top="16px"
          left="24px"
          right="24px"
          height="2px"
          backgroundColor="#E5E7EB"
          zIndex={0}
        />

        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = completedSteps.has(index);
          const isClickable =
            onStepClick && (index <= currentStep || completedSteps.has(index - 1));

          return (
            <Box
              key={step.id}
              flex="1"
              display="flex"
              flexDirection="column"
              alignItems="center"
              position="relative"
              zIndex={1}
            >
              <Box
                width="32px"
                height="32px"
                borderRadius="50%"
                backgroundColor={isActive ? '#3B82F6' : isCompleted ? '#10B981' : '#FFFFFF'}
                borderWidth="2px"
                borderColor={isActive ? '#3B82F6' : isCompleted ? '#10B981' : '#E5E7EB'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor={isClickable ? 'pointer' : 'default'}
                transition="all 0.2s"
                onClick={() => isClickable && onStepClick(index)}
                _hover={
                  isClickable
                    ? {
                        transform: 'scale(1.1)',
                      }
                    : {}
                }
              >
                {isCompleted ? (
                  <CheckIcon color="#FFFFFF" />
                ) : (
                  <Text fontSize="14px" fontWeight="600" color={isActive ? '#FFFFFF' : '#6B7280'}>
                    {index + 1}
                  </Text>
                )}
              </Box>

              <Text
                fontSize="12px"
                marginTop="8px"
                color={isActive ? '#1F2937' : '#6B7280'}
                fontWeight={isActive ? '600' : '400'}
                textAlign="center"
              >
                {step.title}
              </Text>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// Branching wizard helper
export function useBranchingWizard<T extends FieldValues>(
  form: UseFormReturn<T>,
  branches: Record<string, (data: T) => string | null>
) {
  const [currentBranch, setCurrentBranch] = React.useState<string>('default');

  const evaluateBranch = React.useCallback(() => {
    const data = form.getValues();

    for (const [, condition] of Object.entries(branches)) {
      const result = condition(data);
      if (result) {
        setCurrentBranch(result);
        return result;
      }
    }

    return 'default';
  }, [form, branches]);

  return {
    currentBranch,
    evaluateBranch,
    setCurrentBranch,
  };
}
